<template>
  <v-card class="credit-history" flat>
    <!-- Top Bar -->
    <div class="top-bar">
      <div class="heading">
        <div class="title">Kredit shartnoma tarixi</div>
        <div class="subtitle">
          <span class="contract-label">Shartnoma:</span>
          <span class="contract-number">
        {{ contractNumber || ('#' + contractId) }}
      </span>
        </div>
      </div>

      <div class="actions" style="display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 8px;">
        <v-btn
            class="btn-filter"
            :class="{ 'btn-filter--active': showFilters }"
            small
            text
            @click="showFilters = !showFilters"
            style="text-transform: none; font-weight: 500;"
        >
          <v-icon left small>mdi-filter</v-icon>
          {{ showFilters ? 'Yashirish' : 'Filtrlar' }}
        </v-btn>

        <v-btn
            v-if="canCreate"
            class="btn-create"
            :loading="loading"
            @click="openCreate"
            style="margin-right: 5px; background:#1976d2; color:#fff; font-weight:600; text-transform:uppercase;"
        >
          <v-icon color="#FFF" left>mdi-plus</v-icon>
          Yangi shartnoma
        </v-btn>
      </div>

      <div v-if="showFilters" class="filters" style="margin-top: 12px; width: 100%;">
        <div class="filters__group">
          <v-text-field
              v-model="filters.contractNumber"
              label="Shartnoma raqami"
              outlined
              dense
              clearable
              hide-details
              class="filters__input"
              :disabled="loading"
              @keyup.enter="applyFilterQuick"
          />

          <!-- From -->
          <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="290"
              max-width="290"
              content-class="ux-calendar"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                  v-model="filters.from"
                  label="Sana (dan)"
                  outlined dense readonly hide-details
                  class="filters__input filters__input--date"
                  prepend-inner-icon="mdi-calendar"
                  clearable :disabled="loading"
                  v-bind="attrs" v-on="on"
                  @click:clear="filters.from = null; applyFilterQuick()"
              />
            </template>
            <v-card class="date-picker-card" flat>
              <v-date-picker
                  no-title
                  :first-day-of-week="1"
                  :locale="calendarLocale"
                  :header-date-format="calendarHeader"
                  :weekday-format="calendarWeekday"
                  :month-format="calendarMonth"
                  v-model="filters.from"
                  color="primary"
                  scrollable
                  @input="fromMenu = false; applyFilterQuick()"
              />
            </v-card>
          </v-menu>

          <!-- To -->
          <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="290"
              max-width="290"
              content-class="ux-calendar"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                  v-model="filters.to"
                  label="Sana (gacha)"
                  outlined dense readonly hide-details
                  class="filters__input filters__input--date"
                  prepend-inner-icon="mdi-calendar"
                  clearable :disabled="loading"
                  v-bind="attrs" v-on="on"
                  @click:clear="filters.to = null; applyFilterQuick()"
              />
            </template>
            <v-card class="date-picker-card" flat>
              <v-date-picker
                  no-title
                  :first-day-of-week="1"
                  :locale="calendarLocale"
                  :header-date-format="calendarHeader"
                  :weekday-format="calendarWeekday"
                  :month-format="calendarMonth"
                  v-model="filters.to"
                  color="primary"
                  scrollable
                  @input="toMenu = false; applyFilterQuick()"
              />
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div class="summary">

      <div class="summary__card">
        <div class="summary__icon">
          <v-icon>mdi-clock-outline</v-icon>
        </div>
        <div class="summary__body">
          <div class="summary__label">Umumiy kerak</div>
          <div class="summary__value">{{ totals.due | nf }}</div>
        </div>
      </div>

      <div class="summary__card">
        <div class="summary__icon">
          <v-icon>mdi-check-circle</v-icon>
        </div>
        <div class="summary__body">
          <div class="summary__label">Umumiy to'langan</div>
          <div class="summary__value">{{ totals.paid | nf }}</div>
        </div>
      </div>

      <div class="summary__card">
        <div
            class="summary__icon"
            :class="{
            'is-negative': totals.balance > 0,
            'is-positive': totals.balance < 0,
            'is-zero': totals.balance === 0
          }"
        >
          <v-icon :color="getBalanceColor(totals.balance)">mdi-scale-balance</v-icon>
        </div>
        <div class="summary__body">
          <div class="summary__label">Qoldiq (so'm)</div>
          <div class="summary__value" :class="balanceClass(totals.balance)">
            {{ totals.balance | nf }}
          </div>
        </div>
      </div>
    </div>

    <!-- Inline Payment Panel (opens when a row is clicked) -->
    <v-dialog v-model="showPaymentPanel" persistent max-width="1200" :fullscreen="$vuetify.breakpoint.smAndDown" class="payment-modal">
      <v-card class="payment-modal-card">
        <div class="payment-modal-header">
          <h2 class="payment-modal-title">Kredit to'lovlari</h2>
          <v-btn icon @click="showPaymentPanel = false" class="payment-modal-close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div ref="paymentPanel" class="payment-modal-body">
          <credit-payment-panel
              :key="selectedHistoryId"
              :contract-id="contractId"
              :history-id="selectedHistoryId"
              :reloadHistory="reload"
          />
        </div>
      </v-card>
    </v-dialog>

    <!-- Loading -->
    <div v-if="loading" class="loading">
      <v-skeleton-loader type="table"/>
    </div>

    <!-- Table -->
    <v-data-table
        v-else
        :headers="headers"
        :items="items"
        :options="options"
        :server-items-length="total"
        class="table"
        dens
        hide-default-footer
        item-key="id"
        @click:row="rowClicked"
    >
      <template v-slot:item.index="{ index }">
        <div class="index">
          {{ (options.page - 1) * options.itemsPerPage + index + 1 }}
        </div>
      </template>

      <template v-slot:item.date="{ item }">
        <v-chip
            outlined small label
            class="chip chip--date no-row-click"
            @click.stop="openCellEdit(item, 'date')"
        >
          <v-icon left small>mdi-pencil</v-icon>
          {{ fmtDate(item.date) }}
        </v-chip>
      </template>

      <template v-slot:item.amountDue="{ item }">
        <v-chip
            outlined small label
            class="chip chip--amount no-row-click"
            @click.stop="openCellEdit(item, 'amountDue')"
        >
          <v-icon left small>mdi-pencil</v-icon>
          {{ money(item.amountDue) }}
        </v-chip>
      </template>

      <template v-slot:item.amountPaid="{ item }">
        <v-chip
            outlined small label
            class="chip chip--amount no-row-click"
            @click.stop="
                  selectedHistoryId = item.id || null;
                  showPaymentPanel = true
            "
        >
          <v-icon left small>mdi-pencil</v-icon>
          {{ money(item.amountPaid) }}
        </v-chip>
      </template>

      <template v-slot:item.balance="{ item }">
        <v-chip
            small :label="true"
            class="chip chip--balance no-row-click"
            :outlined="calcBalance(item) === 0"
            @click.stop
        >
          {{ money(item.balance != null ? item.balance : calcBalance(item)) }}
        </v-chip>
      </template>

      <template v-slot:item.contractNumber="{ item }">
        <span class="contract-number">
          {{ item.contractNumber }}
        </span>
      </template>

      <template v-slot:item.note="{ item }">
        <v-chip
            outlined small label
            class="chip chip--note no-row-click"
            @click.stop="openCellEdit(item, 'note')"
        >
          <v-icon v-if="!item.note" left small>mdi-pencil</v-icon>
          <span class="note-ellipsis">{{ item.note || "Izoh qo'shish" }}</span>
        </v-chip>
      </template>

      <template v-slot:item.contractFile="{ item }">
        <v-btn
            outlined small
            class="btn-contract no-row-click" color="#2c3e50"
            @click.stop="generateContract(item)"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Shartnoma
        </v-btn>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="actions no-row-click">
          <v-btn v-if="canUpdate" icon small class="actions__btn" color="primary" @click.stop="openEdit(item)">
            <v-icon style="color: #00274c" small>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn v-if="canDelete" icon small class="actions__btn" color="error" @click.stop="removeItem(item)">
            <v-icon small style="color: #c62828">mdi-trash-can</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Empty history: no "Ma'lumot yo'q" text or icon, just an empty table. An empty span keeps Vuetify from falling back to its own "No data available". -->
      <template v-slot:no-data>
        <span></span>
      </template>
    </v-data-table>

    <!-- Pagination -->
    <div v-if="!loading && pages > 1" class="pager">
      <v-pagination
          v-model="options.page"
          :length="pages"
          color="#00274c"
          class="pager__ui"
          @input="onPageChange"
      />
    </div>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog.open" max-width="560px" persistent class="dlg">
      <v-card
          :class="['dlg__card', dialog.editing ? 'is-edit' : 'is-create']"
          :style="{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
        }"
      >
        <div
            class="dlg__head"
            :style="{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #edf2f7',
            background: '#fafafa',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }"
        >
          <div class="dlg__title-wrap" :style="{ display: 'flex', alignItems: 'center', minWidth: 0 }">
            <div
                class="dlg__icon"
                :style="{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }">
              <v-icon small color="#00274c">{{ dialog.editing ? 'mdi-pencil' : 'mdi-note-plus-outline' }}</v-icon>
            </div>

            <div class="dlg__texts" :style="{ display: 'flex', flexDirection: 'column', minWidth: 0 }">
              <h2
                  class="dlg__title"
                  :style="{
                  fontSize: '18px',
                  lineHeight: 1.2,
                  fontWeight: 600,
                  color: '#111827',
                  margin: 0,
                  padding: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }"
              >
                {{ dialog.editing ? 'Yozuvni tahrirlash' : 'Yangi shartnoma' }}
              </h2>

              <div
                  class="dlg__subtitle"
                  :style="{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#6b7280'
                }"
              >
                <v-icon x-small class="mr-1" color="#6b7280" style="margin-right:6px">mdi-file-document</v-icon>
                <span :style="{ overflow: 'hidden', textOverflow: 'ellipsis' }">
                  {{ contractNumber || ('#' + contractId) }}
                </span>
              </div>
            </div>
          </div>

          <v-btn
              icon small class="dlg__close"
              @click="dialog.open=false"
              aria-label="Yopish"
              :style="{ color: '#6b7280' }"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="dlg__divider" style="display:none"/>

        <v-card-text class="dlg__body" :style="{ padding: '16px 16px 8px 16px', background: '#ffffff' }">
          <v-form ref="form" lazy-validation class="form">
            <!-- Date -->
            <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                min-width="290"
                max-width="290"
                content-class="ux-calendar"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="form.date"
                    label="Sana"
                    outlined dense required clearable
                    :rules="[ruleRequired, ruleDate]"
                    placeholder="YYYY-MM-DD"
                    class="form__field"
                    v-bind="attrs" v-on="on"
                    :style="{ marginBottom: '12px' }"
                    @input="onDateInput"
                    @blur="onDateBlur"
                    @keyup.enter="onDateEnter"
                />
              </template>

              <v-card class="date-picker-card" flat>
                <v-date-picker
                    no-title
                    :first-day-of-week="1"
                    :locale="calendarLocale"
                    :header-date-format="calendarHeader"
                    :weekday-format="calendarWeekday"
                    :month-format="calendarMonth"
                    v-model="form.date"
                    color="primary"
                    scrollable
                    @input="dateMenu=false"
                />
              </v-card>
            </v-menu>

            <v-text-field
                v-model="moneyInput.due"
                label="To'lanishi kerak"
                type="text"
                outlined dense required
                :rules="[ruleNonNegative]"
                class="form__field"
                @focus="moneyFocus.paid=true"
                @blur="moneyFocus.paid=false; syncMoney('due')"
                @input="onMoneyInput('due', $event)"
                :style="{ marginBottom: '12px' }"
            />

            <v-textarea
                v-model="form.note"
                label="Izoh"
                outlined auto-grow rows="2" dense
                class="form__field form__field--wide"
                :style="{ marginBottom: '4px' }"
            />
          </v-form>
        </v-card-text>

        <v-divider class="dlg__divider" style="display:none"/>

        <v-card-actions
            class="dlg__actions"
            :style="{
            padding: '12px 16px',
            borderTop: '1px solid #edf2f7',
            background: '#fafafa',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px'
          }"
        >
          <v-spacer/>
          <v-btn
              outlined
              @click="dialog.open=false"
              :style="{
                border: '1px solid #e5e7eb',
                color: '#00274c',
                background: '#f9fafb',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                padding: '6px 16px'
              }"
          >
            <v-icon left small style="margin-right:6px">mdi-close</v-icon>
            Bekor qilish
          </v-btn>

          <v-btn
              :loading="saving"
              depressed color="primary"
              @click="submit"
              :style="{
                background: '#00274c',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                padding: '6px 18px'
              }"
          >
            <v-icon left small style="margin-right:6px">mdi-check</v-icon>
            Saqlash
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cell edit dialog -->
    <v-dialog v-model="cell.open" max-width="480px" class="dlg">
      <v-card class="dlg__card">
        <div class="dlg__head" style="display: flex; justify-content: space-between; padding: 12px">
          <h3 class="dlg__title">{{ cell.title }}</h3>
          <v-btn icon small @click="closeCell">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider/>

        <v-card-text class="dlg__body" style="margin-top: 20px">
          <div v-if="cell.field === 'date'">
            <v-menu
                v-model="cellDateMenu"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                min-width="290"
                max-width="290"
                content-class="ux-calendar"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="cell.value"
                    label="Sana"
                    outlined dense required readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs" v-on="on"
                />
              </template>
              <v-card class="date-picker-card" flat>
                <v-date-picker
                    no-title
                    :first-day-of-week="1"
                    :locale="calendarLocale"
                    :header-date-format="calendarHeader"
                    :weekday-format="calendarWeekday"
                    :month-format="calendarMonth"
                    v-model="cell.value"
                    color="primary"
                    scrollable
                    @input="cellDateMenu=false"
                />
              </v-card>
            </v-menu>
          </div>

          <v-text-field
              v-else-if="cell.field === 'amountDue' || cell.field === 'amountPaid'"
              v-model.number="cell.value"
              :label="cell.title"
              type="number" step="0.01"
              outlined dense required
              :rules="[ruleNonNegative]"
              @keyup.enter="saveCell"
              autofocus
          />

          <v-textarea
              v-else-if="cell.field === 'note'"
              v-model="cell.value"
              :label="cell.title"
              outlined auto-grow rows="3" dense
              @keyup.ctrl.enter="saveCell"
              autofocus
          />

          <v-alert v-else type="warning" outlined class="ma-0">
            Ustun tahrirlash qo'llab-quvvatlanmaydi.
          </v-alert>
        </v-card-text>

        <v-card-actions class="dlg__actions">
          <v-spacer/>
          <v-btn outlined @click="closeCell" :style="{
                border: '1px solid #e5e7eb',
                color: '#00274c',
                background: '#f9fafb',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                padding: '6px 16px'
              }"
          >
            <v-icon left small style="margin-right:6px">mdi-close</v-icon>
            Bekor qilish
          </v-btn>
          <v-btn :loading="saving" depressed color="primary" @click="saveCell" :style="{
                background: '#00274c',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                padding: '6px 18px'
              }"
          >
            <v-icon left small style="margin-right:6px">mdi-check</v-icon>
            Saqlash
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        top right
        timeout="3000"
        class="snack"
    >
      <div class="snack__content">
        <v-icon v-if="snackbar.color !== 'error'" class="mr-2">mdi-check-circle</v-icon>
        <v-icon v-else class="mr-2" color="white">mdi-alert-circle</v-icon>
        {{ snackbar.text }}
      </div>
    </v-snackbar>

    <app-confirm
      v-model="deleteConfirm"
      :text="$t('Are you sure you want to delete?')"
      :confirm-text="$t('Delete')"
      @accept="confirmRemove"
    />
  </v-card>
</template>

<script>
import axios from 'axios';
import calendar from '@/mixins/calendar';
import AppConfirm from '@/components/modals/app-confirm.vue';
import CreditPaymentPanel from '@/components/CreditPaymentPanel.vue';

export default {
  name: 'CreditHistory',
  mixins: [calendar],
  components: { CreditPaymentPanel, AppConfirm },
  props: {
    application: {type: Object, required: true },
    contractId: { type: Number, required: true },
    contractNumber: { type: String, default: '' },
    // Which academic year's contract table contractId comes from. The two tables number their rows
    // independently, so the same id names a different student in each and the API cannot guess.
    studyYear: { type: String, default: '2025-2026' },
    // The panel whose CRUD permissions gate this tab — one per contract year.
    permissionPanel: { type: String, default: 'KONTRAKT_25_26' },
    apiBase: { type: String, default: 'credit/history' },
    contractEndpoint: { type: String, default: '/admin/contract2025/contract' },
    templateId: { type: Number, default: 9000 }
  },
  data: () => ({
    deleteConfirm: false,
    deleteTarget: null,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    headers: [
      { text: '№', value: 'index', sortable: false, width: 70 },
      { text: 'Sana', value: 'date', sortable: false },
      { text: 'Shartnoma raqami', value: 'contractNumber', sortable: false },
      { text: "To'lanishi kerak", value: 'amountDue' },
      { text: "To'langan", value: 'amountPaid' },
      { text: 'Qoldiq', value: 'balance', sortable: false },
      { text: 'Izoh', value: 'note', sortable: false },
      { text: 'Shartnoma', value: 'contractFile', sortable: false, width: 140 },
      { text: '', value: 'actions', sortable: false, width: 120 }
    ],
    items: [],
    total: 0,
    loading: false,

    // table options (no .sync / no watcher)
    options: { page: 1, itemsPerPage: 100, sortBy: ['date'], sortDesc: [true] },

    // filters
    filters: { contractNumber: '', from: null, to: null },

    // dialogs & forms
    dialog: { open: false, editing: false },
    form: { id: null, date: null, amountDue: 0, amountPaid: 0, note: '' },
    dateMenu: false,
    fromMenu: false,
    toMenu: false,

    // money input helpers (formatted on blur)
    moneyInput: { due: '', paid: '' },
    moneyFocus: { due: false, paid: false },

    // cell edit
    cell: { open: false, field: null, title: '', value: null, item: null },
    cellDateMenu: false,
    editableLabels: {
      date: 'Sana',
      amountDue: "To'lanishi kerak",
      amountPaid: "To'langan",
    },

    // ui
    saving: false,
    snackbar: { show: false, text: '', color: 'success' },

    // internal
    _reloadPending: false,
    _deb: null,

    // inline payment panel control
    showPaymentPanel: false,
    selectedHistoryId: null,
    showFilters: false,
  }),
  computed: {
    totals() {
      const sum = (arr, key) => arr.reduce((a, b) => a + Number((b && b[key]) || 0), 0);
      const due = sum(this.items, 'amountDue');
      const paid = sum(this.items, 'amountPaid');
      return { due, paid, balance: due - paid };
    },
    pages() {
      const size = Number(this.options.itemsPerPage || 100);
      return Math.max(1, Math.ceil((this.total || 0) / (size > 0 ? size : 100)));
    }
  },
  filters: {
    nf(v) {
      return new Intl.NumberFormat('uz-UZ').format(Number(v || 0));
    }
  },
  async mounted() {
    this.canCreate = await this.hasRole(this.permissionPanel, "CREATE");
    this.canUpdate = await this.hasRole(this.permissionPanel, "UPDATE");
    this.canDelete = await this.hasRole(this.permissionPanel, "DELETE");
    if (!this.form.date) this.form.date = this.todayIso();
    this.syncMoney('due');
    this.syncMoney('paid');
    this.reload();
  },
  beforeDestroy() {
    if (this._deb) clearTimeout(this._deb);
  },
  methods: {
    // Guarded row click: opens inline CreditPaymentPanel for that history item
    rowClicked(item, event) {
      const el = event && event.target ? event.target : null;
      if (el && (el.closest('.no-row-click') || el.closest('.v-btn') || el.closest('.v-icon') || el.closest('.v-chip'))) {
        return;
      }
      if (!item) return;

      this.selectedHistoryId = item.id || null;
      this.showPaymentPanel = true;

      this.$nextTick(() => {
        const n = this.$refs.paymentPanel;
        if (n && n.scrollIntoView) n.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    onPageChange(p) {
      this.options.page = p || 1;
      this.reloadQueued();
    },

    onDateInput(val) {
      if (typeof val !== 'string') return;
      const cleaned = val.replace(/[.\s/]/g, '-').replace(/--+/g, '-').slice(0, 10);
      this.form.date = cleaned;
    },
    onDateBlur() {
      const norm = this.normalizeDate(this.form.date);
      if (norm) this.form.date = norm;
    },
    onDateEnter() {
      this.onDateBlur();
      this.dateMenu = false;
    },
    normalizeDate(s) {
      if (!s) return null;
      const t = String(s).trim();
      let u = t.replace(/[.\s/]/g, '-');
      if (/^\d{4}-\d{2}-\d{2}$/.test(u) && this.isValidYMD(u)) return u;

      if (/^\d{2}-\d{2}-\d{4}$/.test(u)) {
        const [dd, mm, yyyy] = u.split('-').map(n => parseInt(n, 10));
        if (this.isValidParts(yyyy, mm, dd)) {
          return `${yyyy.toString().padStart(4, '0')}-${mm.toString().padStart(2, '0')}-${dd.toString().padStart(2, '0')}`;
        }
        return null;
      }

      if (/^\d{8}$/.test(u)) {
        const a = u.slice(0, 4), b = u.slice(4, 6), c = u.slice(6, 8);
        const first = parseInt(a, 10);
        if (first >= 1900 && first <= 2100) {
          const yyyy = first, mm = parseInt(b, 10), dd = parseInt(c, 10);
          if (this.isValidParts(yyyy, mm, dd)) {
            return `${yyyy}-${b}-${c}`;
          }
        } else {
          const dd = parseInt(u.slice(0, 2), 10);
          const mm = parseInt(u.slice(2, 4), 10);
          const yyyy = parseInt(u.slice(4, 8), 10);
          if (this.isValidParts(yyyy, mm, dd)) {
            return `${yyyy}-${mm.toString().padStart(2, '0')}-${dd.toString().padStart(2, '0')}`;
          }
        }
        return null;
      }
      return null;
    },
    isValidYMD(ymd) {
      const [y, m, d] = ymd.split('-').map(n => parseInt(n, 10));
      return this.isValidParts(y, m, d);
    },
    isValidParts(y, m, d) {
      if (!(y >= 1900 && y <= 2100)) return false;
      if (!(m >= 1 && m <= 12)) return false;
      const dim = this.daysInMonth(y, m);
      return d >= 1 && d <= dim;
    },
    daysInMonth(y, m) {
      return new Date(y, m, 0).getDate();
    },

    // ---- RULES ----
    ruleRequired(v) {
      return !!v || 'Majburiy';
    },
    ruleDate(v) {
      return !v || this.normalizeDate(v) ? true : "Sana 'YYYY-MM-DD' formatida bo'lishi kerak";
    },

    // ===== Money helpers =====
    parseMoney(v) {
      if (v === '' || v === null || v === undefined) return 0;
      const s = String(v).replace(/\s/g, '').replace(/[^\d.,-]/g, '');
      const normalized = s.replace(/,/g, '.');
      const num = Number(normalized);
      return isNaN(num) ? 0 : num;
    },
    formatMoney(n) {
      return new Intl.NumberFormat('uz-UZ').format(Number(n || 0));
    },
    onMoneyInput(which, val) {
      const num = this.parseMoney(val);
      if (which === 'due') this.form.amountDue = num;
      else this.form.amountPaid = num;
      this.moneyInput[which] = val;
    },
    syncMoney(which) {
      if (which === 'due') this.moneyInput.due = this.formatMoney(this.form.amountDue);
      else this.moneyInput.paid = this.formatMoney(this.form.amountPaid);
    },

    // ===== Validation =====
    ruleNonNegative(v) {
      const n = this.parseMoney(v);
      return (v === '' || v === null || (!isNaN(n) && n >= 0)) || "0 dan kichik bo'lmasin";
    },

    // ===== Filters / reload =====
    applyFilterQuick() {
      this.options.page = 1;
      this.reloadQueued();
    },
    reloadQueued() {
      if (this.loading) {
        this._reloadPending = true;
        return;
      }
      if (this._deb) clearTimeout(this._deb);
      this._deb = setTimeout(() => this.reload(), 120);
    },
    async reload() {
      if (this.loading) return;

      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        this.toast("'Sana (dan)' 'Sana (gacha)'-dan katta bo'lmasin.", 'error');
        return;
      }

      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options || {};
        let size = Number(itemsPerPage || 100);
        if (size <= 0) size = 100;

        const p = new URLSearchParams();
        p.set('page', Math.max(0, (Number(page || 1) - 1)));
        p.set('size', size);
        p.set('sort', (sortBy && sortBy[0]) || 'date');
        p.set('direction', (sortDesc && sortDesc[0]) ? 'DESC' : 'ASC');
        p.set('contractId', this.contractId);
        p.set('studyYear', this.studyYear);
        if (this.filters.contractNumber) p.set('contractNumber', this.filters.contractNumber);
        if (this.filters.from) p.set('from', this.filters.from);
        if (this.filters.to) p.set('to', this.filters.to);

        const url = `${this.apiBase}?${p.toString()}`;
        const res = await axios.get(url);
        const data = res && res.data ? res.data : res;

        const raw = data.content || data.items || [];
        this.items = raw.map(it => ({
          ...it,
          amountDue: Number(it.amountDue || 0),
          amountPaid: Number(it.amountPaid || 0)
        }));

        this.total = (data.totalElements != null)
            ? data.totalElements
            : (data.total != null ? data.total : this.items.length);
      } catch (e) {
        console.error('Failed to load history', e);
        this.toast('Yuklashda xatolik.', 'error');
      } finally {
        const again = this._reloadPending;
        this._reloadPending = false;
        this.loading = false;
        if (again) this.reload();
      }
    },

    // ===== Row & CRUD =====
    openCreate() {
      this.form = { id: null, date: this.todayIso(), amountDue: 0, amountPaid: 0, note: '' };
      this.dialog = { open: true, editing: false };
      this.$nextTick(() => {
        this.syncMoney('due');
        this.syncMoney('paid');
      });
    },
    openEdit(item) {
      const it = Array.isArray(item) ? item[0] : item;
      this.form = {
        id: it.id,
        date: it.date || this.todayIso(),
        amountDue: Number(it.amountDue || 0),
        amountPaid: Number(it.amountPaid || 0),
        note: it.note || ''
      };
      this.dialog = { open: true, editing: true };
      this.$nextTick(() => {
        this.syncMoney('due');
        this.syncMoney('paid');
      });
    },
    async submit() {
      const ok = this.$refs.form && this.$refs.form.validate ? this.$refs.form.validate() : true;
      if (!ok) return;

      this.saving = true;
      try {
        const payload = {
          contractId: this.contractId,
          studyYear: this.studyYear,
          date: this.form.date,
          amountDue: Number(this.form.amountDue),
          amountPaid: Number(this.form.amountPaid),
          note: this.form.note || null
        };

        if (this.dialog.editing && this.form.id != null) {
          await axios.put(`${this.apiBase}/${this.form.id}`, payload);
          this.toast('Yozuv yangilandi.');
        } else {
          await axios.post(this.apiBase, payload);
          this.toast("Yozuv qo'shildi.");
        }
        this.dialog.open = false;
        this.reloadQueued();
      } catch (e) {
        console.error('Save failed', e);
        this.toast('Saqlashda xatolik.', 'error');
      } finally {
        this.saving = false;
      }
    },

    // ===== Cell edit =====
    openCellEdit(item, field) {
      if (!['date', 'amountDue', 'amountPaid', 'note'].includes(field)) return;
      this.cell.item = item;
      this.cell.field = field;
      this.cell.title = this.editableLabels[field] || 'Tahrirlash';
      this.cell.value = field === 'date'
          ? (item.date || this.todayIso())
          : field === 'note'
              ? (item.note || '')
              : Number(item[field] || 0);
      this.cell.open = true;
    },
    closeCell() {
      this.cell = { open: false, field: null, title: '', value: null, item: null };
    },
    async saveCell() {
      if (!this.cell.item || !this.cell.field) return;
      if ((this.cell.field === 'amountDue' || this.cell.field === 'amountPaid') && Number(this.cell.value) < 0) {
        this.toast("Qiymat manfiy bo'lmasin.", 'error');
        return;
      }

      const it = this.cell.item;
      const payload = {
        contractId: it.contractId || this.contractId,
        studyYear: it.studyYear || this.studyYear,
        date: (this.cell.field === 'date') ? this.cell.value : it.date,
        amountDue: (this.cell.field === 'amountDue') ? Number(this.cell.value) : Number(it.amountDue || 0),
        amountPaid: (this.cell.field === 'amountPaid') ? Number(this.cell.value) : Number(it.amountPaid || 0),
        note: (this.cell.field === 'note') ? (this.cell.value || null) : (it.note || null)
      };

      this.saving = true;
      try {
        await axios.put(`${this.apiBase}/${it.id}`, payload);
        this.toast('Yangilandi.');
        this.closeCell();
        this.reloadQueued();
      } catch (e) {
        console.error('Cell save failed', e);
        this.toast('Tahrirlashda xatolik.', 'error');
      } finally {
        this.saving = false;
      }
    },

    // ===== Misc =====
    removeItem(item) {
      this.deleteTarget = item;
      this.deleteConfirm = true;
    },

    async confirmRemove() {
      const item = this.deleteTarget;
      this.deleteTarget = null;
      if (!item) return;
      try {
        await axios.delete(`${this.apiBase}/${item.id}`);
        this.toast("Yozuv o'chirildi.");
        this.reloadQueued();
      } catch (e) {
        console.error('Delete failed', e);
        this.toast("O'chirishda xatolik.", 'error');
      }
    },

    async generateContract(item) {
      const contractId = this.contractId;
      if (!contractId) {
        this.$emit("error", "Contract ID not found.");
        return;
      }

      this.loadingId = item.id;

      try {
        const locale = this.$i18n.locale;
        const res = await axios.get(
            `/credit/history/contract?contract_id=${contractId}&history_id=${item.id}&locale=${locale}`,
            {responseType: "blob"}
        );

        const blob = new Blob([res.data], {type: "application/pdf"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${this.application.firstName + " " + this.application.lastName + " " + this.application.middleName}.pdf`;
        link.click();
      } catch (e) {
        console.error(e);
        this.$emit("error", "Xatolik yuz berdi");
      } finally {
        this.loadingId = null;
      }
    },

    todayIso() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    money(v) {
      return new Intl.NumberFormat('uz-UZ').format(Number(v || 0));
    },
    fmtDate(d) {
      if (!d) return '';
      const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
      return dt.toLocaleDateString('uz-UZ');
    },
    calcBalance(it) {
      return Number(it.amountDue || 0) - Number(it.amountPaid || 0);
    },
    balanceClass(v) {
      return v > 0 ? 'is-negative' : v < 0 ? 'is-positive' : 'is-zero';
    },
    getBalanceColor(v) {
      return v > 0 ? 'error' : v < 0 ? 'success' : 'grey';
    },
    toast(text, type = 'success') {
      this.snackbar = { show: true, text, color: type === 'error' ? 'error' : 'success' };
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
/* --- Minimal Light-Black Theme -------------------------------------------- */
$bg: #ffffff;
$ink: #111; // light-black text
$muted: #555; // secondary text
$line: #e6e6e6;
$soft: #f6f6f6;
$shadow: 0 6px 16px rgba(0, 0, 0, .06);
$radius: 12px;
$brand: #00274c; // same navy as the action buttons and "Yangi shartnoma"
$brand-soft: #eef2f7;

/* Shell */
.credit-history {
  background: #f7f8fa;
  border: 1px solid $line;
  border-radius: $radius !important;
  box-shadow: $shadow;
  overflow: hidden;

  /* Top */
  .top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 18px 20px;
    border-bottom: 1px solid $line;
    background: $bg;

    .heading {
      .title {
        font-size: 20px;
        font-weight: 800;
        color: $brand;
        margin: 0 0 4px;
        letter-spacing: -.01em;
      }

      .subtitle {
        font-size: 13px;
        color: $muted;
      }

      .contract-label {
        font-weight: 600;
      }

      .contract-number {
        display: inline-block;
        margin-left: 8px;
        padding: 4px 10px;
        border-radius: 8px;
        background: $brand-soft;
        color: $brand;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
    }


    .filters {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      justify-content: space-between;

      &__group {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      &__input {
        min-width: 200px;
      }

      &__input--date {
        min-width: 170px;
      }

      .btn-create {

        background: #00274c !important; /* pure black */
        color: #fefefe !important; /* bright white text */
        font-weight: 500; /* extra bold */
        font-size: 16px; /* bigger text */
        letter-spacing: 0.8px;
        padding: 20px 26px; /* larger, balanced spacing */
        border: 1px solid #1c1c1c; /* subtle border */
        border-radius: 12px; /* modern rounded corners */
        text-transform: none;
        transition: background 0.25s ease, transform 0.15s ease;
        box-shadow: none !important; /* stripped shadow */
      }

      .btn-create:hover {
        background: #00274c !important; /* subtle dark lift */
        border-color: #333;
        transform: translateY(-1px);
      }

      .btn-create:active {
        background: #111 !important;
        transform: translateY(0);
      }

      .btn-create .v-icon {
        font-size: 20px;
        margin-right: 10px;
        color: #ccc;
        transition: color 0.2s ease;
      }

      .btn-create:hover .v-icon {
        color: #fff;
      }

    }
  }

  /* Summary */
  .summary {
    display: grid;
    gap: 14px;
    padding: 16px 20px;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));

    &__card {
      border: 1px solid $line;
      border-radius: $radius;
      padding: 16px;
      display: flex;
      align-items: center;
      gap: 12px;
      background: $bg;
      transition: box-shadow .15s ease;

      &:hover {
        box-shadow: $shadow;
      }

      .summary__icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        display: grid;
        place-items: center;
        background: $brand-soft;
        color: $brand;

        .v-icon {
          color: $brand;
        }

        &.is-negative {
          background: #ffeeee;
        }

        &.is-positive {
          background: #e9f8ee;
        }

        &.is-zero {
          background: $soft;
        }
      }

      .summary__label {
        font-size: 12px;
        color: $muted;
        font-weight: 600;
      }

      .summary__value {
        font-size: 20px;
        font-weight: 800;
        color: $ink;
        font-variant-numeric: tabular-nums;
      }

      .is-negative {
        color: #c62828;
      }

      .is-positive {
        color: #2e7d32;
      }

      .is-zero {
        color: #6b7280;
      }
    }

    &__actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: center;

      .btn-pdf {
        border-radius: 10px;
        text-transform: none;
        font-weight: 700;
        background: $bg;
        border: 1px solid $line;

        &:hover {
          background: $soft;
        }
      }
    }
  }

  /* Loading */
  .loading {
    padding: 16px 20px;
  }

  /* Table */
  .table {
    background: transparent;
    margin: 0 20px 20px;
    border-radius: $radius;
    overflow: hidden;
    box-shadow: none;
    font-family: "Inter", sans-serif;
    font-variant-numeric: tabular-nums;

    // a global rule sets the system font on the inner spans (header labels, chip and button
    // text), so every element is covered; icons keep their icon font
    ::v-deep th,
    ::v-deep th *:not(.v-icon),
    ::v-deep td,
    ::v-deep td *:not(.v-icon) {
      font-family: "Inter", sans-serif !important;
    }

    ::v-deep td,
    ::v-deep td *:not(.v-icon) {
      letter-spacing: normal !important;
    }

    ::v-deep .v-data-table__wrapper {
      background: $bg;
      border: 1px solid $line;
      border-radius: $radius;
    }

    ::v-deep thead {
      background: $soft;

      th {
        background: transparent !important;
        color: $muted !important;
        font-weight: 800 !important;
        font-size: 11px !important;
        text-transform: uppercase;
        letter-spacing: .05em;
        border-bottom: 1px solid $line !important;
        padding: 14px 12px !important;
        position: sticky;
        top: 0;
        z-index: 1;
      }
    }

    ::v-deep tbody tr {
      border-bottom: 1px solid $line !important;
      transition: background-color .12s ease;

      &:hover {
        background: $soft;
      }
    }

    ::v-deep td {
      padding: 14px 12px !important;
      border-bottom: 1px solid $line !important;
    }

    .index {
      font-weight: 700;
      color: $muted;
    }

    .contract-number {
      color: $muted;
      font-size: 13px;
    }

    .chip {
      cursor: pointer;
      font-weight: 600;
      transition: transform .12s ease;
      border-color: #bbb !important;
      color: $ink;

      &:hover {
        transform: scale(1.03);
      }

      &--amount {
        font-variant-numeric: tabular-nums;
      }

      &--balance {
        font-variant-numeric: tabular-nums;
        font-weight: 800;
      }

      &--note .note-ellipsis {
        max-width: 220px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .btn-contract {
      border-radius: 8px;
      text-transform: none;
      font-weight: 600;
      font-size: 12px;
      border: 1px solid #d5dbe0;
      background-color: #f9f9f9;
    }

    .actions {
      display: flex;
      gap: 6px;
    }

    .actions__btn {
      border-radius: 8px;
    }

    .empty {
      text-align: center;
      padding: 40px 16px;
      color: $muted;

      h3 {
        margin: 10px 0 6px;
        color: $ink;
      }

      p {
        margin: 0;
        font-size: 13px;
      }
    }
  }

  /* Pager */
  .pager {
    padding: 0 20px 20px;
    display: flex;
    justify-content: center;

    &__ui {
      ::v-deep .v-pagination__item,
      ::v-deep .v-pagination__navigation {
        border-radius: 10px !important;
        margin: 0 3px;
        background-color: $brand-soft !important;
        border: none !important;
        box-shadow: none !important;
        color: $brand;
        font-weight: 600;
        transition: background-color .15s ease;

        &:hover {
          background-color: #dfe6ef !important;
        }
      }

      ::v-deep .v-pagination__navigation .v-icon {
        color: $brand;
      }

      ::v-deep .v-pagination__navigation--disabled {
        opacity: .45;
      }

      ::v-deep .v-pagination__item--active {
        background-color: $brand !important;
        color: #fff !important;
      }
    }
  }

  /* Dialogs */
  .dlg {
    &__card {
      background: $bg !important;
      border: 1px solid $line !important;
      border-radius: $radius !important;
      box-shadow: $shadow !important;
      overflow: hidden;
    }

    &__head {
      padding: 14px 20px;
      border-bottom: 1px solid $line;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__title-wrap {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      display: grid;
      place-items: center;
      background: $soft;
      color: $ink;
      border: 1px solid $line;

      ::v-deep .v-icon {
        font-size: 20px;
      }
    }

    &__texts {
      display: flex;
    }

    &__title {
      margin: 0;
      font-size: 19px;
      font-weight: 900;
      color: $ink;
      line-height: 1.18;
      letter-spacing: .01em;
    }

    &__subtitle {
      font-size: 12px;
      color: $muted;
      display: flex;
      align-items: center;
      font-weight: 600;
    }

    &__close {
      color: $muted;

      &:hover {
        color: $ink;
      }
    }

    &__divider {
      border-color: $line !important;
    }

    &__body {
      padding: 18px 20px !important;
    }

    &__actions {
      padding: 14px 20px !important;
      border-top: 1px solid $line;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .form {
      display: grid;
      gap: 16px;

      &__field {
      }

      &__field--wide {
        grid-column: 1 / -1;
      }
    }

    .btn-ghost {
      text-transform: none;
      font-weight: 700;
      border-radius: 10px !important;
      border: 1px solid $line !important;
      background: $bg !important;
      color: $ink !important;

      &:hover {
        background: $soft !important;
      }
    }

    .btn-primary {
      text-transform: none;
      font-weight: 800;
      border-radius: 10px !important;
      background: $ink !important;
      color: #fff !important;
      border: 1px solid $ink !important;
      box-shadow: none !important;

      &:hover {
        filter: brightness(.92);
      }
    }
  }

  /* Date picker popover */
  .date-picker-card {
    border: 1px solid $line !important;
    border-radius: $radius !important;
    box-shadow: $shadow !important;
  }

  /* Snackbar */
  .snack {
    ::v-deep .v-snack__wrapper {
      border-radius: 10px !important;
      box-shadow: $shadow !important;
      background: $ink;
      color: #fff;
    }

    .snack__content {
      display: flex;
      align-items: center;
      gap: 10px;
      font-weight: 600;
    }
  }

  /* Inputs / Menus */
  ::v-deep .v-text-field, ::v-deep .v-textarea {
    .v-input__slot {
      background: $bg;
      border: 1px solid $line;
      border-radius: 10px;
    }

    .v-label {
      color: $muted;
      font-weight: 600;
    }
  }

  ::v-deep .v-menu__content {
    border: 1px solid $line !important;
    border-radius: $radius !important;
    box-shadow: $shadow !important;
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .top-bar {
      flex-direction: column;
      align-items: stretch;
    }
    .summary {
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    }
    .table {
      margin: 0 16px 16px;
    }
    .pager {
      padding: 0 16px 16px;
    }
  }
  @media (max-width: 768px) {
    .summary {
      grid-template-columns: 1fr;
    }
    .top-bar .filters .filters__input,
    .top-bar .filters .filters__input--date {
      min-width: 150px;
    }
  }
}

/* Helper class to mark interactive cells that shouldn't trigger row click */
.no-row-click {
  pointer-events: auto;
}

//&__actions { display: flex; align-items: center; gap: 10px; }

.btn-panel {
  min-width: 260px;               // wider presence
  height: 52px;                   // comfy height
  padding: 0 16px;
  border-radius: 12px !important;
  border: 1px solid $line !important;
  background: $bg !important;
  color: $ink !important;
  text-transform: none;
  font-weight: 800;
  font-size: 16px;
  letter-spacing: .2px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: none !important;
  transition: background .18s ease, color .18s ease, border-color .18s ease;

  ::v-deep .v-icon { font-size: 22px !important; }

  &:hover { background: $soft !important; }
  &.is-on {
    background: $ink !important;
    color: #fff !important;
    border-color: $ink !important;
  }
}

/* subtle “feel” with chevron rotation when open */
.btn-panel__chev {
  margin-left: 6px;
  transition: transform .18s ease, opacity .18s ease;
}
.is-on .btn-panel__chev { transform: rotate(180deg); }

.payment-modal {
  ::v-deep .v-dialog__content {
    .v-card {
      height: 90vh;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }
}

.payment-modal-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.payment-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #e5e7eb;
}

.payment-modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.payment-modal-close {
  .v-icon {
    color: #6b7280;
  }
}

.payment-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;

  // Ensure the panel fits well
  ::v-deep .credit-payment-panel {
    border-radius: 0 !important;
    box-shadow: none !important;
    height: 100%;

    .cpp__top,
    .cpp__summary {
      padding-left: 20px;
      padding-right: 20px;
    }

    .cpp__table {
      margin-left: 20px;
      margin-right: 20px;
      margin-bottom: 20px;

      ::v-deep .v-data-table__wrapper {
        border-radius: 0 !important;
        border-left: none;
        border-right: none;
        border-top: none;
      }
    }
  }
}

.btn-filter {
  color: #2c3e50 !important;
  background: rgba(243, 244, 246, 0.8) !important;
  border: 1px solid rgba(107, 114, 128, 0.3) !important;
  border-radius: 10px !important;
  padding: 6px 16px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(107, 114, 128, 0.1) !important;
  cursor: pointer !important;
  min-width: auto !important;
  height: auto !important;

  &:focus {
    outline: none !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(0, 39, 76, 0.2) !important;
  }

  &:hover {
    background: rgba(243, 244, 246, 1) !important;
    color: #00274c !important;
    border-color: rgba(107, 114, 128, 0.5) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(107, 114, 128, 0.2) !important;
    transform: translateY(-1px) !important;
  }

  &--active {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%) !important;
    color: #111827 !important;
    border-color: #d1d5db !important;
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.25), 0 0 0 3px rgba(107, 114, 128, 0.15) !important;

    &:hover {
      background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%) !important;
      transform: translateY(-1px) !important;
    }
  }

  .v-icon {
    color: inherit !important;
    font-size: 18px !important;
  }
}

@media (max-width: 768px) {
  .btn-filter {
    padding: 6px 12px !important;
    font-size: 14px !important;

    .v-icon {
      font-size: 16px !important;
    }
  }
}

.filters__group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filters__input {
  min-width: 200px;

  &--date {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .filters__group {
    flex-direction: column;
    align-items: stretch;

    .filters__input {
      min-width: 100%;
    }
  }
}

.btn-create {
  background: linear-gradient(135deg, #00274c 0%, #00274c 100%) !important;
  color: #ffffff !important;
  border: 1px solid rgba(75, 85, 99, 0.3) !important;
  border-radius: 12px !important;
  padding: 8px 20px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
  cursor: pointer !important;
  min-width: auto !important;
  height: auto !important;
  position: relative !important;
  overflow: hidden !important;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #00274c 0%, #00274c 100%) !important;
    color: #f9fafb !important;
    border-color: rgba(75, 85, 99, 0.5) !important;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.25) !important;

    &::before {
      left: 100%;
    }
  }

  &:focus {
    outline: none !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(0, 39, 76, 0.3) !important;
  }

  &:active {
    transform: translateY(0) !important;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 2px -1px rgba(0, 0, 0, 0.1) !important;
  }

  .v-icon {
    color: #ffffff !important;
    font-size: 20px !important;
    margin-right: 6px !important;
  }

  &[disabled] {
    background: #4b5563 !important;
    color: #9ca3af !important;
    border-color: rgba(75, 85, 99, 0.5) !important;
    box-shadow: none !important;
    transform: none !important;

    .v-icon {
      color: #9ca3af !important;
    }
  }
}

@media (max-width: 768px) {
  .btn-create {
    padding: 8px 16px !important;
    font-size: 14px !important;

    .v-icon {
      font-size: 18px !important;
    }
  }
}
@media (max-width: 600px) {
  .payment-modal ::v-deep .v-dialog__content .v-card { height: 100vh; max-height: 100vh; border-radius: 0; }
}
/* Dark theme: calm surfaces and hairline borders, same base palette as App.vue's dark rules */
$d-bg: #16171e;
$d-surface: #1c1d25;
$d-raised: #23242d;
$d-line: rgba(255, 255, 255, .07);
$d-line-strong: rgba(255, 255, 255, .12);
$d-text: #d5d6de;
$d-text-strong: #ecedf2;
$d-muted: #8b8d9e;

.credit-history.theme--dark {
  background: $d-bg !important;
  border-color: $d-line;
  box-shadow: none;

  .top-bar {
    background: $d-surface;
    border-bottom-color: $d-line;

    .heading .title {
      color: $d-text-strong;
    }

    .heading .subtitle {
      color: $d-muted;
    }

    .heading .contract-number {
      background: $d-raised;
      color: $d-text;
    }

    .btn-filter {
      background: $d-raised !important;
      border-color: $d-line-strong !important;
      color: $d-text !important;

      .v-icon {
        color: $d-text !important;
      }
    }
  }

  .summary__card {
    background: $d-surface;
    border-color: $d-line;

    &:hover {
      box-shadow: none;
      border-color: $d-line-strong;
    }

    .summary__icon {
      background: $d-raised;

      .v-icon {
        color: $d-text;
      }
    }

    .summary__label {
      color: $d-muted;
    }

    .summary__value {
      color: $d-text-strong;
    }
  }

  .table {
    ::v-deep .v-data-table__wrapper {
      background: $d-surface;
      border-color: $d-line;
    }

    ::v-deep thead {
      background: $d-surface;

      th {
        color: $d-muted !important;
        border-bottom: 1px solid $d-line !important;
      }
    }

    ::v-deep tbody tr,
    ::v-deep tbody tr:nth-child(even) {
      background: transparent !important;
    }

    ::v-deep tbody tr:hover {
      background: rgba(255, 255, 255, .025) !important;
    }

    ::v-deep td {
      color: $d-text !important;
      border-bottom: 1px solid $d-line !important;
    }

    ::v-deep tbody tr:last-child td {
      border-bottom: none !important;
    }

    .index,
    .contract-number,
    .due-cell__formula {
      color: $d-muted;
    }

    .chip {
      background: rgba(255, 255, 255, .03) !important;
      border-color: $d-line-strong !important;
      color: $d-text !important;

      &:hover {
        border-color: rgba(255, 255, 255, .22) !important;
      }

      .v-icon {
        color: $d-muted !important;
      }
    }

    .chip--balance:not(.v-chip--outlined) {
      background: $d-raised !important;
      border-color: transparent !important;
    }

    .btn-contract {
      background: transparent !important;
      border-color: $d-line-strong !important;
      color: $d-text !important;

      .v-icon {
        color: $d-muted !important;
      }
    }

    // the edit pencil has an inline navy colour that disappears on dark
    .actions__btn.primary--text .v-icon {
      color: $d-text !important;
    }
  }

  .pager__ui {
    ::v-deep .v-pagination__item,
    ::v-deep .v-pagination__navigation {
      background-color: $d-raised !important;
      color: $d-text !important;

      &:hover {
        background-color: #2b2c36 !important;
      }
    }

    ::v-deep .v-pagination__navigation .v-icon {
      color: $d-text !important;
    }

    ::v-deep .v-pagination__item--active {
      background-color: #ffd451 !important;
      color: #111318 !important;
    }
  }
}
</style>
